/**
 * stations — was die neun Slots der Kette bedeuten, je in einem Satz ohne Jargon.
 *
 * Die Kopfzeile der Achse zeigt neun Kürzel (`SCR MIR PAR …`). Für den, der die
 * Pipeline gebaut hat, ist das eine Landkarte; für alle anderen sind es neun
 * Buchstabenpaare. Diese Datei ist die EINE Quelle der Klartext-Bedeutung — sie
 * speist beides:
 *
 *   `STATIONS`        → die Legende im aufklappbaren `ActivityGuide`
 *   `EXPLAINED_SLOTS` → dieselben neun Slots mit der Bedeutung im `title`
 *                       der Tabellen-Kopfzeile. Ein Tooltip kostet keine Fläche,
 *                       also darf die Erklärung dort dauerhaft hängen.
 *
 * Abgeleitet wird beides aus `STAGE_SLOTS` (queue/stageChain) — die Reihenfolge
 * und der Bestand der Slots gehören dorthin, nicht hierher. Kommt ein Slot dazu,
 * fällt das im Test `stations.test.ts` auf, statt still als leere Zeile in der
 * Legende zu landen.
 */
import type { FlowSlotDef } from "@/components/flow";
import { STAGE_SLOTS } from "@/components/queue/stageChain";

export interface Station {
  /** Slot-Id — identisch mit dem Stage-Namen im Laufzettel. */
  id: string;
  /** Kürzel, wie es in der Kopfzeile steht. */
  label: string;
  /** Was die Station tut — ein Satz, ohne Vorwissen lesbar. */
  means: string;
  /**
   * Ehrliche Einschränkung, soweit es eine gibt. Erscheint als Fußnote unter
   * der Legende — nicht als Kleingedrucktes, sondern als eigener Absatz.
   */
  caveat?: string;
}

interface Meaning {
  means: string;
  caveat?: string;
}

/**
 * Die Bedeutungen. `caveat` steht nur dort, wo die Station NICHT das tut, was
 * ihr Name verspricht — beides ist am Runner nachgelesen, nicht geraten.
 */
const MEANINGS: Record<string, Meaning> = {
  scrape: { means: "Nachsehen, ob es beim Herausgeber etwas Neues gibt" },
  mirror: { means: "Das Dokument unverändert spiegeln (Beweisebene)" },
  parse: { means: "Aus dem Dokument strukturierte Daten machen" },
  generate_bpmn: { means: "Prozessdiagramme erzeugen" },
  generate_dmn: {
    means: "Entscheidungstabellen erzeugen",
    caveat:
      "generate_dmn ist ein Stub: als Konsument deklariert, erzeugt aber nichts. " +
      "Ein grüner Slot heißt hier „ohne Fehler durchgelaufen“, nicht „es liegt eine Entscheidungstabelle vor“.",
  },
  generate_ticket: { means: "Aufgaben für Menschen ableiten" },
  jira_promote: {
    means: "Tickets ins Vorgangssystem heben",
    caveat:
      "jira_promote ist keine offizielle Station: STAGE_NAMES im Runner " +
      "(maco-runner/src/maco_runner/models.py) kennt acht. Die neunte existiert nur, weil das " +
      "Pydantic-Modell extra: „allow“ gesetzt hat, und taucht in genau einem Laufzettel auf. " +
      "Deshalb steht bei den anderen drei Läufen an dieser Stelle die Ghost-Linie — " +
      "„trifft nicht zu“, nicht „fehlgeschlagen“.",
  },
  process_repo: { means: "Ergebnisse in die Ausgabe-Repos je Marktrolle legen" },
  camunda_deploy: { means: "In der Prozess-Engine scharf schalten" },
};

/** Die neun Stationen in der Reihenfolge der Kette. */
export const STATIONS: Station[] = STAGE_SLOTS.map((slot) => ({
  id: slot.id,
  label: slot.label,
  means: MEANINGS[slot.id]?.means ?? "",
  caveat: MEANINGS[slot.id]?.caveat,
}));

/** Die Fußnoten der Legende — die Stationen, die nicht halten, was sie heißen. */
export const STATION_CAVEATS: Station[] = STATIONS.filter((s) => s.caveat);

/**
 * Dieselben neun Slots wie überall, nur mit Klartext im `full` — und `full` ist
 * genau das, was `MicroFlowHeader` als `title` an jedes Kürzel hängt. Ids,
 * Labels und Reihenfolge bleiben unangetastet, sonst stünden die Slots der
 * Zeilen nicht mehr unter ihren Namen.
 */
export const EXPLAINED_SLOTS: FlowSlotDef[] = STAGE_SLOTS.map((slot) => {
  const means = MEANINGS[slot.id]?.means;
  return { ...slot, full: means ? `${slot.id} — ${means}` : (slot.full ?? slot.label) };
});
